import { useState, useEffect, useMemo } from 'react'
import {
  CollectionReference,
  collection,
  getDocs,
  getFirestore,
  query,
} from 'firebase/firestore'
import FirestoreUser from '../../../../types/FirestoreUser'
import useHandleError from '../../../../hooks/useHandleError'
import { useSearchStore, useListStore } from './useUsersStore'
import { buildQueryConstraints } from '../utils/buildQueryConstraints'
import { fetchUserReports } from '../utils/fetchUserReports'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

const LIMIT = 1000

/**
 * Custom hook for fetching and managing user queries
 * Handles search, pagination, and report fetching
 *
 * @param list - Whether this is a list view (vs search view)
 * @param studies - Available studies mapping
 * @returns Object containing docs, hasMore, loading state, search function, and reports
 */
export function useUsersQuery(list: boolean, studies: Record<string, string>) {
  const useStore = useMemo(() => {
    return list ? useListStore : useSearchStore
  }, [list])

  const docs = useStore((state) => state.docs)
  const hasMore = useStore((state) => state.hasMore)
  const filter = useStore((state) => state.filter)
  const input = useStore((state) => state.input)
  const [loading, setLoading] = useState<'more' | 'initial' | false>(false)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const handleError = useHandleError()

  async function search(clear: boolean) {
    setLoading(clear ? 'initial' : 'more')
    try {
      const constraints = buildQueryConstraints(
        filter,
        input,
        studies,
        !clear && docs.length > 0 ? docs[docs.length - 1] : undefined,
        LIMIT
      )

      const snap = await getDocs(
        query(
          collection(
            primaryServices.firestore,
            'users'
          ) as CollectionReference<FirestoreUser>,
          ...constraints
        )
      )

      // CLIENT-SIDE FILTERING for studies (Firestore can't query nested object fields)
      let filteredDocs = snap.docs

      // Check if this is a study filter (the filter value is a key in the studies map)
      if (filter && Object.keys(studies).includes(filter as string)) {
        const studyName = studies[filter as string]

        filteredDocs = snap.docs.filter((doc) => {
          const userData = doc.data()
          const userStudies = userData.studies || []

          // Check if any study matches (handle both string and StudyData formats)
          const hasStudy = userStudies.some((study: any) => {
            if (typeof study === 'string') {
              // String format: check if it matches ID or name
              return study === filter || study === studyName
            } else if (study && typeof study === 'object' && study.name) {
              // StudyData object format: check the name field
              return study.name === filter || study.name === studyName
            }
            return false
          })

          return hasStudy
        })
      }

      // Fetch reports for users (use filtered docs)
      const reports = await fetchUserReports(
        filteredDocs,
        useStore.getState().reports,
        clear
      )

      setIsTableLoading(false)

      useStore.setState({
        docs: clear ? filteredDocs : [...docs, ...filteredDocs],
        hasMore: snap.size >= LIMIT,
        reports,
      })
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (filter) search(true)
    // eslint-disable-next-line
  }, [filter, input])

  // Automatically load all remaining alphabetical pages in background so A-Z scroll works
  useEffect(() => {
    if (list && docs.length > 0 && hasMore && loading === false) {
      // Trigger next page load silently for any List view
      search(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, hasMore, loading, filter, list])

  return {
    docs,
    hasMore,
    loading,
    isTableLoading,
    search,
    reports: useStore.getState().reports,
    setIsTableLoading,
    useStore,
  }
}
